import { computed, inject } from '@angular/core';
import {
  ActivitiesService,
  Activity,
  AgenciesService,
  Agency,
  EntitiesService,
  Entity,
  Profile,
  ProfilesService,
  Region,
  RegionsService,
} from '@api';
import {
  patchState,
  signalStore,
  withComputed,
  withHooks,
  withMethods,
  withState,
} from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { pipe, switchMap, tap } from 'rxjs';
import { UserStore } from './user.store';

export interface ConstStateInterface {
  regions: Region[];
  agencies: Agency[];
  activities: Activity[];
  entities: Entity[];
  profiles: Profile[];
}

export const ConstStore = signalStore(
  withState<ConstStateInterface>({
    regions: [],
    agencies: [],
    activities: [],
    entities: [],
    profiles: [],
  }),
  withMethods(
    (
      store,
      userStore = inject(UserStore),
      agenciesService = inject(AgenciesService),
      activitiesService = inject(ActivitiesService),
      regionsService = inject(RegionsService),
      entityService = inject(EntitiesService),
      profilesService = inject(ProfilesService)
    ) => ({
      profileWithoutAdmin() {
        return store
          .profiles()
          .filter((p) => p.ref !== userStore.user().profileRef);
      },
      legalEntityLogo() {
        return store
          .entities()
          .find((entity) => entity.id === userStore.contract().legalEntityId)
          ?.uuid;
      },
      getRegionById(id: number): Region | undefined {
        return store
          .regions()
          .find((r) => r.id === this.getAgencyById(id)?.regionId);
      },
      getAgencyById(id: number): Agency | undefined {
        return store.agencies().find((r) => r.id === id);
      },
      getAgenciesByRegionId(id?: number): Agency[] | undefined {
        return store.agencies().filter((r) => r.regionId === id);
      },
      getActivityById(id: number): Region | undefined {
        return store.activities().find((r) => r.id === id);
      },
      getProfilById(id: number): Profile | undefined {
        return store.profiles().find((p) => p.id === id);
      },
      getProfilByKeycloakTag(keycloakTag?: string): Profile | undefined {
        return store.profiles().find((p) => p.ref === keycloakTag);
      },
      getEntityById(id: number): Entity | undefined {
        return store.entities().find((p) => p.id === id);
      },
      loadAgencies: rxMethod<void>(
        pipe(
          switchMap(() => {
            return agenciesService.getAgencies().pipe(
              tap((agencies) => {
                patchState(store, { agencies });
              })
            );
          })
        )
      ),
      loadActivities: rxMethod<void>(
        pipe(
          switchMap(() => {
            return activitiesService.getActivities().pipe(
              tap((activities) => {
                patchState(store, { activities });
              })
            );
          })
        )
      ),
      loadRegions: rxMethod<void>(
        pipe(
          switchMap(() => {
            return regionsService.getRegions().pipe(
              tap((regions) => {
                patchState(store, { regions });
              })
            );
          })
        )
      ),
      loadEntity: rxMethod<void>(
        pipe(
          switchMap(() => {
            return entityService.getEntities().pipe(
              tap((entities: any[]) => {
                patchState(store, { entities });
              })
            );
          })
        )
      ),
      loadProfiles: rxMethod<void>(
        pipe(
          switchMap(() => {
            return profilesService.getProfiles().pipe(
              tap((profiles: Profile[]) => {
                patchState(store, { profiles });
              })
            );
          })
        )
      ),
    })
  ),
  withHooks({
    onInit(store) {
      store.loadAgencies();
      store.loadRegions();
      store.loadActivities();
      store.loadEntity();
      store.loadProfiles();
    },
  })
);
